import { app, on } from '../app/state';
import { bestStreak, currentStreak, getDay, lastNDays, type DaySummary } from '../core/history';
import { dayKey, fmtHM, parseDay } from '../core/util';
import { $, svgEl } from './dom';
import { onLayout } from './layout';
import { onTabShown } from './sheet';

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const longDate = (k: string) =>
  parseDay(k).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/* ---------- shared tooltip + table helpers ---------- */

function tooltip(fig: HTMLElement) {
  const tip = document.createElement('div');
  tip.className = 'chart-tip';
  tip.hidden = true;
  fig.appendChild(tip);
  return {
    show(text: string, mark: Element) {
      tip.textContent = text;
      tip.hidden = false;
      const fr = fig.getBoundingClientRect();
      const mr = mark.getBoundingClientRect();
      const x = mr.left + mr.width / 2 - fr.left;
      const half = tip.offsetWidth / 2;
      tip.style.left = `${Math.min(Math.max(x, half), fr.width - half)}px`;
      tip.style.top = `${mr.top - fr.top - 8}px`;
    },
    hide: () => (tip.hidden = true),
  };
}

function dataTable(caption: string, rows: DaySummary[]) {
  const t = document.createElement('table');
  t.className = 'chart-table';
  t.hidden = true;
  const cap = document.createElement('caption');
  cap.textContent = caption;
  t.appendChild(cap);
  t.insertAdjacentHTML(
    'beforeend',
    '<thead><tr><th scope="col">Day</th><th scope="col">Sessions</th><th scope="col">Minutes</th></tr></thead>',
  );
  const tb = document.createElement('tbody');
  for (const r of rows) {
    const tr = document.createElement('tr');
    for (const v of [longDate(r.key), String(r.sessions), String(r.minutes)]) {
      const td = document.createElement('td');
      td.textContent = v;
      tr.appendChild(td);
    }
    tb.appendChild(tr);
  }
  t.appendChild(tb);
  return t;
}

function tableToggle(fig: HTMLElement, table: HTMLTableElement, svg: SVGSVGElement) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'linkbtn chart-toggle';
  const paint = () => {
    b.textContent = table.hidden ? 'TABLE' : 'CHART';
    b.setAttribute('aria-pressed', String(!table.hidden));
  };
  b.addEventListener('click', () => {
    table.hidden = !table.hidden;
    svg.classList.toggle('is-hidden', !table.hidden);
    paint();
  });
  paint();
  fig.appendChild(b);
}

const describe = (d: DaySummary) =>
  `${longDate(d.key)} — ${d.sessions} session${d.sessions === 1 ? '' : 's'} · ${d.minutes} min`;

/* ---------- 7-day column chart (single series: focus minutes) ---------- */

function renderWeek(now: number) {
  const fig = $('#weekChart');
  fig.innerHTML = '';
  const days = lastNDays(app.data.history, 7, now);
  const W = 360;
  const H = 150;
  const top = 18;
  const base = 120;
  const max = Math.max(60, ...days.map((d) => d.minutes));
  const slot = W / 7;
  const bw = 22;
  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, class: 'week', 'aria-hidden': 'true' });
  svg.appendChild(svgEl('line', { x1: 0, x2: W, y1: base + 0.5, y2: base + 0.5, class: 'axis' }));
  const tip = tooltip(fig);
  const maxIdx = days.reduce((bi, d, i, a) => (d.minutes > a[bi]!.minutes ? i : bi), 0);
  days.forEach((d, i) => {
    const cx = slot * i + slot / 2;
    const h = d.minutes ? Math.max(4, ((base - top) * d.minutes) / max) : 0;
    const x = cx - bw / 2;
    const y = base - h;
    if (h) {
      // 4px rounded data-end, square at the baseline
      const r = Math.min(4, h);
      svg.appendChild(
        svgEl('path', {
          d: `M${x},${base}V${y + r}Q${x},${y} ${x + r},${y}H${x + bw - r}Q${x + bw},${y} ${x + bw},${y + r}V${base}Z`,
          class: `bar${i === 6 ? ' today' : ''}`,
        }),
      );
    }
    if (d.minutes && (i === 6 || i === maxIdx)) {
      const v = svgEl('text', { x: cx, y: y - 6, class: 'val', 'text-anchor': 'middle' });
      v.textContent = String(d.minutes);
      svg.appendChild(v);
    }
    const lab = svgEl('text', {
      x: cx,
      y: base + 18,
      class: `lab${i === 6 ? ' today' : ''}`,
      'text-anchor': 'middle',
    });
    lab.textContent = i === 6 ? 'TODAY' : DOW[parseDay(d.key).getDay()]!;
    svg.appendChild(lab);
    // hit target larger than the mark
    const hit = svgEl('rect', { x: slot * i, y: 0, width: slot, height: base, class: 'hit' });
    hit.addEventListener('pointerenter', () => tip.show(describe(d), hit));
    hit.addEventListener('pointerleave', tip.hide);
    svg.appendChild(hit);
  });
  fig.appendChild(svg);
  const total = days.reduce((a, d) => a + d.minutes, 0);
  const cap = document.createElement('figcaption');
  cap.textContent = `${total} focus minutes in the last 7 days`;
  fig.append(cap);
  const table = dataTable('Focus minutes, last 7 days', days);
  fig.append(table);
  tableToggle(fig, table, svg);
}

/* ---------- 12-week heatmap (sequential, one hue) ---------- */

function renderHeatmap(now: number) {
  const fig = $('#heatmap');
  fig.innerHTML = '';
  const weeks = 12;
  const today = new Date(now);
  // grid ends on the current week (rows Mon..Sun)
  const dowMon = (today.getDay() + 6) % 7;
  const n = (weeks - 1) * 7 + dowMon + 1;
  const days = lastNDays(app.data.history, n, now);
  const s = app.data.settings;
  const target = s.dailyGoal ? s.dailyGoal * s.focus : 200;
  const level = (m: number) => (m <= 0 ? 0 : Math.min(4, Math.ceil((4 * m) / target)));
  const cell = 13;
  const gap = 3;
  const left = 28;
  const W = left + weeks * (cell + gap);
  const H = 7 * (cell + gap) + 2;
  const svg = svgEl('svg', {
    viewBox: `0 0 ${W} ${H}`,
    width: Math.round(W * 1.15),
    height: Math.round(H * 1.15),
    class: 'heat',
    'aria-hidden': 'true',
  });
  ['MON', '', 'WED', '', 'FRI', '', 'SUN'].forEach((t, r) => {
    if (!t) return;
    const l = svgEl('text', { x: 0, y: r * (cell + gap) + cell - 3, class: 'lab' });
    l.textContent = t;
    svg.appendChild(l);
  });
  const tip = tooltip(fig);
  days.forEach((d, i) => {
    const col = Math.floor(i / 7);
    const row = i % 7;
    const rect = svgEl('rect', {
      x: left + col * (cell + gap),
      y: row * (cell + gap),
      width: cell,
      height: cell,
      rx: 3,
      class: `hm l${level(d.minutes)}${d.key === dayKey(now) ? ' today' : ''}`,
    });
    rect.addEventListener('pointerenter', () => tip.show(describe(d), rect));
    rect.addEventListener('pointerleave', tip.hide);
    svg.appendChild(rect);
  });
  fig.appendChild(svg);
  const legend = document.createElement('div');
  legend.className = 'hm-legend';
  legend.setAttribute('aria-hidden', 'true');
  legend.innerHTML = `<span>LESS</span>${[0, 1, 2, 3, 4].map((l) => `<i class="hm l${l}"></i>`).join('')}<span>MORE</span>`;
  const active = days.filter((d) => d.sessions > 0).length;
  const cap = document.createElement('figcaption');
  cap.textContent = `${active} active day${active === 1 ? '' : 's'} in the last ${weeks} weeks`;
  fig.append(legend, cap);
  const table = dataTable(`Daily focus, last ${weeks} weeks`, days.filter((d) => d.sessions > 0).reverse());
  fig.append(table);
  tableToggle(fig, table, svg);
}

/* ---------- numbers, goal, log ---------- */

function renderNumbers(now: number) {
  const d = app.data;
  const day = getDay(d.history, dayKey(now));
  const streak = currentStreak(d.history, d.streakCarry, now);
  const goal = d.settings.dailyGoal;
  for (const id of ['#scSess', '#stSess']) $(id).textContent = String(day.sessions);
  for (const id of ['#scMin', '#stMin']) $(id).textContent = String(day.minutes);
  for (const id of ['#scStreak', '#stStreak']) $(id).textContent = String(streak);
  $('#statCol').setAttribute(
    'aria-label',
    `Today: ${day.sessions} sessions, ${day.minutes} focus minutes, ${streak} day streak — open stats`,
  );

  const box = $('#goalBox');
  box.hidden = !goal;
  if (goal) {
    $('#goalText').textContent = `${day.sessions} / ${goal}`;
    const bar = $('#goalBar');
    bar.setAttribute('aria-valuemax', String(goal));
    bar.setAttribute('aria-valuenow', String(Math.min(day.sessions, goal)));
    bar.classList.toggle('met', day.sessions >= goal);
    $('#goalFill').style.width = `${Math.min(100, (day.sessions / goal) * 100)}%`;
  }
  const best = bestStreak(d.history, d.streakCarry);
  $('#stAll').textContent =
    `ALL TIME — ${d.totals.sessions} SESSIONS · ${(d.totals.minutes / 60).toFixed(1)} H · BEST STREAK ${best} D`;

  const ul = $('#logList');
  ul.innerHTML = '';
  for (const e of day.log) {
    const li = document.createElement('li');
    const time = document.createElement('span');
    time.className = 'l-time';
    time.textContent = fmtHM(e.ts);
    const task = document.createElement('span');
    task.className = 'l-task';
    task.textContent = e.task ?? '';
    const min = document.createElement('span');
    min.className = 'l-min';
    min.textContent = `${e.min} MIN${e.interruptions ? ` · ${e.interruptions}✕` : ''}`;
    if (e.interruptions) min.title = `${e.interruptions} interruption${e.interruptions > 1 ? 's' : ''}`;
    li.append(time, task, min);
    ul.appendChild(li);
  }
  $('#logEmpty').hidden = day.log.length > 0;
}

let statsVisible = false;
let statsDocked = false; // Logbook shows the charts on the main screen
function render() {
  const now = Date.now();
  renderNumbers(now);
  if (statsVisible || statsDocked) {
    renderWeek(now);
    renderHeatmap(now);
  }
}

export function initStats() {
  on('stats', render);
  on('settings', render);
  onTabShown((t) => {
    statsVisible = t === 'stats';
    if (statsVisible) render();
  });
  onLayout((l) => {
    statsDocked = l === 'logbook';
    render();
  });
  render();
}
